import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import classNames from 'classnames';
import { getUserSettings } from '@surfline/web-common';
import TideChart from 'components/ForecastGraphTide/components/TideChart';
import GraphTimeline, { TimeTickColor } from 'components/GraphTimeline/GraphTimeline';
import type { MoveTideLabel, TidePoint } from 'types/tide';
import { getChartTide } from 'selectors/graphs';
import { useAppSelector } from 'stores/hooks';
import { convertToMilliseconds, convertToSeconds } from 'utils/time';

import styles from './CurrentTideGraph.module.scss';

const PREVIOUS_DATA_POINTS = 3;
const FUTURE_DATA_POINTS = 13;

const findClosestTimestampIndex = (tideData: TidePoint[], currentTimestamp: number): number => {
  let closestIndex = 0;
  let smallestDifference = Math.abs(tideData[0].timestamp - currentTimestamp);

  for (let i = 1; i < tideData.length; i += 1) {
    const difference = Math.abs(tideData[i].timestamp - currentTimestamp);
    if (difference < smallestDifference && tideData[i].type === 'NORMAL') {
      smallestDifference = difference;
      closestIndex = i;
    }
  }
  return closestIndex;
};

const CurrentTideGraph: React.FC = () => {
  const graphRef = useRef<HTMLDivElement | null>(null);
  const [graphWidth, setGraphWidth] = useState(0);
  const userSettings = useAppSelector(getUserSettings);
  const dateFormat: 'MDY' | 'DMY' = userSettings?.date?.format === 'DMY' ? 'DMY' : 'MDY';
  const tideDataSelector = useAppSelector(getChartTide);
  const { days, error, loading, tideLocation, units } = tideDataSelector;
  const [shouldPadLeft, setShouldPadLeft] = useState(false);
  const [shouldPadRight, setShouldPadRight] = useState(false);

  const slicedData = useMemo(() => {
    setShouldPadLeft(false);
    setShouldPadRight(false);
    if (!days) return undefined;
    let closestStartIndex = 0;

    const trimmedDays: Array<TidePoint[]> = [];

    // Each day has an extra data point at the end with a duplicate timestamp of midnight
    // This needs removing
    days.forEach((day: TidePoint[]) => {
      trimmedDays.push(day.slice(0, -1));
    });

    const flatDays = trimmedDays.flat();

    closestStartIndex = findClosestTimestampIndex(
      flatDays,
      Math.floor(convertToSeconds(Date.now())),
    );

    // We want 3 normal data points before the current time
    let previousCounter = 0;
    let actualStartIndex = closestStartIndex;

    for (let i = closestStartIndex; i >= 0; i -= 1) {
      if (flatDays[i].type === 'NORMAL' && previousCounter <= PREVIOUS_DATA_POINTS) {
        previousCounter += 1;
        actualStartIndex = i;
      }
    }

    let normalCount = 0;
    let closestEndIndex = actualStartIndex + FUTURE_DATA_POINTS;
    for (let i = actualStartIndex; i < flatDays.length; i += 1) {
      if (flatDays[i].type === 'NORMAL') {
        normalCount += 1;
        if (normalCount === FUTURE_DATA_POINTS) {
          closestEndIndex = i;
          break;
        }
      }
    }

    if (
      (flatDays[actualStartIndex] && flatDays[actualStartIndex].type !== 'NORMAL') ||
      (flatDays[actualStartIndex + 1] && flatDays[actualStartIndex + 1].type !== 'NORMAL') ||
      (flatDays[actualStartIndex + 2] && flatDays[actualStartIndex + 2].type !== 'NORMAL')
    ) {
      setShouldPadLeft(true);
    }

    if (
      (flatDays[closestEndIndex] && flatDays[closestEndIndex].type !== 'NORMAL') ||
      (flatDays[closestEndIndex - 1] && flatDays[closestEndIndex - 1].type !== 'NORMAL') ||
      (flatDays[closestEndIndex - 2] && flatDays[closestEndIndex - 2].type !== 'NORMAL') ||
      (flatDays[closestEndIndex - 3] && flatDays[closestEndIndex - 3].type !== 'NORMAL')
    ) {
      setShouldPadRight(true);
    }

    return flatDays.slice(actualStartIndex, closestEndIndex);
  }, [days]);

  const timelinePoints = useMemo(
    () => slicedData?.filter((point: TidePoint) => point.type === 'NORMAL'),
    [slicedData],
  );

  const nowPositionStyle = useMemo(() => {
    if (!slicedData || slicedData.length === 0 || !graphRef.current) return { left: '0px' };

    const now = Date.now();
    const startTimestamp = convertToMilliseconds(slicedData[0].timestamp);
    const endTimestamp = convertToMilliseconds(slicedData[slicedData.length - 1].timestamp);
    const totalDuration = endTimestamp - startTimestamp;

    const currentPosition = now - startTimestamp;

    const percentagePosition = (currentPosition / totalDuration) * 100;

    const leftNowPosition = Math.ceil((percentagePosition / 100) * graphWidth);
    return { left: `${leftNowPosition}px` };
  }, [graphWidth, slicedData]);

  useEffect(() => {
    if (!graphRef.current) {
      return () => {};
    }
    const currentGraphRef = graphRef.current;
    const observer = new ResizeObserver(() => {
      setGraphWidth(currentGraphRef?.clientWidth || 0);
    });

    if (observer.observe) {
      observer.observe(currentGraphRef);
      return () => observer.unobserve(currentGraphRef as Element);
    }
    return () => {};
  }, [graphRef]);

  if (error) return null;

  // IMPORTANT: Below dayIndex is set to 99 to stop the graph id clashing with other graphs on the page
  return (
    <Box
      className={classNames(styles.tideGraph, {
        [styles.padLeft]: shouldPadLeft,
        [styles.padRight]: shouldPadRight,
      })}
      ref={graphRef}
    >
      {loading && <Skeleton animation="pulse" variant="rounded" className={styles.loading} />}
      {days && slicedData && !loading && (
        <Box>
          <Box className={styles.nowContainer} style={nowPositionStyle}>
            <Box className={styles.nowMarker} />
          </Box>
          <TideChart
            data={slicedData}
            tideLocation={tideLocation}
            units={units.tideHeight}
            dateFormat={dateFormat}
            startDate={convertToMilliseconds(slicedData[0].timestamp)}
            endDate={convertToMilliseconds(slicedData[slicedData.length - 1].timestamp)}
            dayIndex={99}
            isTableMode={false}
            showNowPosition
            moveTideLabel={'BOTH' as MoveTideLabel}
          />
          <GraphTimeline
            customDateFormat="haaa"
            day={timelinePoints?.slice(0, -1) as Array<TidePoint>}
            timeTickColor={TimeTickColor.DARK}
            alignStartOnMidnight
            showMarkers
            alignLastLabel
          />
        </Box>
      )}
    </Box>
  );
};

export default CurrentTideGraph;
